import { FormField } from '@/components/ui/Form';
import { DualListBox, Form, FormInput, FormItem, FormLabel } from '@/components/wm';
import { apiRequest } from '@/utils/request';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export function SelectAssets ({ data, onChangeField, onCompleted }) {
    const
        [externalAssetOptions, setExternalAssetOptions] = useState([]),
        [isLoadingAssets, setIsLoadingAssets] = useState(false),
        [totalAssetsCount, setTotalAssetsCount] = useState(0);

    const formSchema = z.object({
        assetsAttributes: z.array(z.any()).refine(
            assets => assets.some(asset => !asset._destroy),
            { message: 'Please add at least one asset' }
        ),
        name: z.string().min(1, 'Name is required')
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            assetsAttributes: data.assetsAttributes || [],
            name: data.name || ''
        },
        mode: 'onTouched'
    });

    const assetsAttributes = form.watch('assetsAttributes');

    // We only want items where _destroy is not true to appear on the right side
    const selectedIdsForList = useMemo(() => {
        return assetsAttributes
            .filter(asset => !asset._destroy)
            .map(asset => String(asset.asset_reference));
    }, [assetsAttributes]);

    const fetchExternalAssets = async (term = '') => {
        setIsLoadingAssets(true);

        try {
            const payload = {
                license_id: data.licenseId,
                partner_id: data.partnerId,
                site_id: data.siteId,
                configuration_id: data.configurationId,
                search_text: term
            };

            const response = await apiRequest('/integrations/external_assets', 'POST', payload);

            // Map API response to labels/values for the CheckboxGroup
            const options = (response.data.assets || []).map(asset => ({
                label: asset.name,
                value: String(asset.asset_reference), // Component uses 'key', not 'value'
                raw: {
                    name: asset.name,
                    asset_reference: asset.asset_reference,
                    local_reference: asset.local_reference || 0,
                    create_work_orders: asset.create_work_orders || true,
                    create_markers: asset.create_markers || true
                }
            }));

            setExternalAssetOptions(options);
            setTotalAssetsCount(response.data.total_count || 0);
        } catch (err) {
            WiseMetering.layout.showTipper('error', 'Failed to fetch assets');
        } finally {
            setIsLoadingAssets(false);
        }
    };

    const combinedOptions = useMemo(() => {
        // Create a Map of the current API results
        const optionsMap = new Map(externalAssetOptions.map(o => [o.value, o]));

        // Add currently selected assets to that map so they always have an option object
        assetsAttributes.forEach(asset => {
            const val = String(asset.asset_reference);
            if (!optionsMap.has(val)) {
                optionsMap.set(val, {
                    label: asset.name,
                    value: val,
                    raw: asset
                });
            }
        });

        return Array.from(optionsMap.values());
    }, [externalAssetOptions, assetsAttributes]);

    const handleAssetsChange = newIdsOnRight => {
        const currentAssets = form.getValues('assetsAttributes') || [];

        // Process ONLY Legacy Assets (Items that have a database 'id')
        const legacyAssets = currentAssets
            .filter(asset => asset.id !== undefined && asset.id !== null)
            .map(asset => ({
                ...asset,
                // If it's not on the right side anymore, tag it for destruction
                _destroy: !newIdsOnRight.includes(String(asset.asset_reference))
            }));

        // Process ONLY New Assets (Items currently selected that are NOT legacy)
        const legacyRefs = new Set(legacyAssets.map(a => String(a.asset_reference)));

        const newActiveAssets = newIdsOnRight
            .filter(id => !legacyRefs.has(id)) // Skip the legacy ones we just handled
            .map(id => {
                // Find the raw data from our options
                const found = combinedOptions.find(opt => opt.value === id);
                // Notice we do NOT add a _destroy tag here at all
                return found ? { ...found.raw } : null;
            })
            .filter(Boolean); // Clean up any nulls

        // Combine them.
        const finalAssets = [...legacyAssets, ...newActiveAssets];

        form.setValue('assetsAttributes', finalAssets, { shouldValidate: true });
        if (onChangeField) {
            onChangeField('assetsAttributes')(finalAssets);
        }
    };

    const errorMessage = useMemo(() => {
        if (externalAssetOptions.length === 0) {
            return i18next.t('alarms.no_alarms_found').capitalize();
        }
        return form.formState.errors.assetsAttributes?.message;
    }, [form.formState.errors.assetsAttributes, externalAssetOptions.length]);

    useEffect(() => {
        fetchExternalAssets();
    }, []);

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid, onCompleted]);

    return (
        <div className="tw-w-full">
            <Form {...form} className="tw-mb-6">
                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{i18next.t('common.name').capitalize()}</FormLabel>
                            <FormInput
                                {...field}
                                onChange={e => {
                                    const val = e.target.value;

                                    field.onChange(val);
                                    if (onChangeField) {
                                        onChangeField('name')(val);
                                    }
                                }}
                            />
                        </FormItem>
                    )}
                />
            </Form>
            {isLoadingAssets ? (
                <p className="tw-text-[12px] tw-ml-2 tw-text-gray-400 tw-mt-1">{i18next.t('glossary.loading').capitalize()}...</p>
            ) : externalAssetOptions.length > 0 ? (
                <p className="tw-text-[12px] tw-ml-2 tw-text-gray-400 tw-mt-1">
                    {
                        totalAssetsCount > externalAssetOptions.length
                            ? i18next.t('integrations.nextbitt.showing_assets', { options: externalAssetOptions.length, total: totalAssetsCount })
                            : i18next.t('integrations.nextbitt.total_assets', { count: totalAssetsCount })
                    }
                </p>
            ) : (
                <p className="tw-text-[12px] tw-ml-2 tw-text-gray-400 tw-mt-1">{i18next.t('integrations.nextbitt.no_assets_found').capitalize()}</p>
            )}
            <DualListBox
                error={errorMessage}
                isLoading={isLoadingAssets}
                onChange={handleAssetsChange}
                onSearchEnter={term => fetchExternalAssets(term)} // Receive term here
                options={combinedOptions}
                searchPlaceholder={i18next.t('integrations.nextbitt.search_assets')}
                selectedIds={selectedIdsForList}
                title={i18next.t('integrations.nextbitt.assets')}
            />
        </div>
    );
}
